import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { createPortal } from 'react-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const SharedAnalysis = () => {
  const { id } = useParams();
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/shared/${id}`);
        setAnalysis(response.data);
      } catch (err) {
        setError('Analysis not found or failed to load.');
      }
    };
    fetchAnalysis();
  }, [id, BACKEND_URL]);

  if (error) return <div>{error}</div>;
  if (!analysis) return <div>Loading...</div>;

  // Render the same as the main analysis tab
  return (
    <div className="results-section">
      <h2>
        Shared Analysis for{' '}
        <a
          href={analysis.metadata?.url}
          target="_blank"
          rel="noopener noreferrer"
          className="glow-link"
        >
          {analysis.metadata?.url
            ?.replace(/^https?:\/\/(www\.)?/, '')
            ?.replace(/\/+$/, '')}
        </a>
      </h2>
      {analysis.metrics && (
        <div className="metrics-visuals">
          <div className="metrics-cards-row">
            <div className="metric-card">
              <div className="metric-value">{analysis.metrics.overall_visibility?.toFixed(1) ?? '--'}%</div>
              <div className="metric-label">Overall Visibility</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{analysis.metrics.weighted_visibility_score?.toFixed(1) ?? '--'}%</div>
              <div className="metric-label">Weighted Score</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{analysis.metrics.average_rank?.toFixed(1) ?? '--'}</div>
              <div className="metric-label">Avg. Rank</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{analysis.metrics.best_rank ?? '--'}</div>
              <div className="metric-label">Best Rank</div>
            </div>
            <div className="metric-card">
              <div className="metric-value">{analysis.metrics.worst_rank ?? '--'}</div>
              <div className="metric-label">Worst Rank</div>
            </div>
          </div>
          <div className="metrics-charts-row">
            {/* Engine Performance Bar Chart */}
            {analysis.metrics.engine_performance && (
              <div className="chart-container">
                <h4>Engine Visibility %</h4>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    key={analysis.id || id}
                    data={Object.entries(analysis.metrics.engine_performance).map(([engine, perf]) => ({
                      engine,
                      visibility: perf.visibility_percentage,
                      weighted: perf.weighted_visibility_score
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="engine" />
                    <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="visibility" fill="#38bdf8" name="Visibility %" isAnimationActive={true} animationDuration={900} />
                    <Bar dataKey="weighted" fill="#06b6d4" name="Weighted %" isAnimationActive={true} animationDuration={900} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {/* Success vs Missed Pie Chart */}
            {(analysis.metrics.successful_queries || analysis.metrics.missed_queries) && (
              <div className="chart-container">
                <h4>Visible vs. Missed</h4>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart key={analysis.id || id}>
                    <Pie
                      data={[
                        { name: 'Visible', value: analysis.metrics.successful_queries?.length || 0 },
                        { name: 'Missed', value: analysis.metrics.missed_queries?.length || 0 }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      fill="#8884d8"
                      dataKey="value"
                      isAnimationActive={true}
                      animationDuration={900}
                    >
                      <Cell key="success" fill="#38bdf8" />
                      <Cell key="missed" fill="#818cf8" />
                    </Pie>
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Grouped queries and results */}
      <div className="grouped-queries-section">
        {analysis.generated_queries && analysis.generated_queries.map((query, idx) => (
          <div key={idx} className="query-group">
            <div className="query-title">{`Query: ${query}`}</div>
            <div className="results-grid">
              {analysis.search_results && analysis.search_results.filter(result => result.query === query).map((result, rIdx) => (
                <div key={rIdx} className="result-card">
                  <div className="result-header">
                    <div className="result-engine">{result.engine}</div>
                    {result.rank
                      ? <div className="result-position ranked">Rank {result.rank}</div>
                      : <div className="result-position not-ranked">Did Not Rank</div>
                    }
                  </div>
                  {result.url && <div className="result-url">{result.url}</div>}
                  <div className="result-snippet">
                    {result.snippet
                      ? result.snippet
                      : result.full_response
                        ? result.full_response.slice(0, 200) + (result.full_response.length > 200 ? "..." : "")
                        : <span className="no-snippet">No preview available.</span>
                    }
                  </div>
                  <button 
                    className="view-full-btn"
                    onClick={() => {
                      setSelectedResponse(result.full_response);
                      setShowModal(true);
                    }}
                  >
                    View Full Response
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Response Modal */}
      {showModal && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: '0px',
            left: '0px',
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '2rem',
            backdropFilter: 'blur(20px)',
            transform: 'translateZ(0)'
          }}
          onClick={() => setShowModal(false)}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(51, 65, 85, 0.9))',
              borderRadius: '20px',
              padding: '3.5rem',
              maxWidth: '900px',
              maxHeight: '80vh',
              overflowY: 'auto',
              position: 'relative',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
              border: '1px solid rgba(51, 65, 85, 0.5)',
              backdropFilter: 'blur(20px)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              style={{
                position: 'absolute',
                top: '2rem',
                right: '2rem',
                background: 'rgba(15, 23, 42, 0.5)',
                border: '1px solid rgba(51, 65, 85, 0.3)',
                fontSize: '1.5rem',
                color: '#cbd5e1',
                cursor: 'pointer',
                width: '3rem',
                height: '3rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.3s ease'
              }}
              onClick={() => setShowModal(false)}
            >
              ×
            </button>
            <div 
              style={{
                fontSize: '1.375rem',
                fontWeight: 700,
                color: '#f8fafc',
                marginBottom: '2rem',
                paddingBottom: '1.25rem',
                borderBottom: '2px solid rgba(51, 65, 85, 0.3)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Full Search Response
            </div>
            <div 
              style={{
                color: '#94a3b8',
                lineHeight: 1.7,
                fontSize: '1.125rem',
                fontWeight: 500
              }}
            >
              <ReactMarkdown>{selectedResponse}</ReactMarkdown>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SharedAnalysis; 